import { useEffect, useRef, useState } from 'react';
import { NotFoundInPage } from '@/components/NotFoundInPage';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import './preauths.css';
import type { Tab, ToSendItem } from './types';
import {
  approvedCountSync,
  decideSentItem,
  dontSend,
  getApprovedSync,
  getItemSync,
  getSentItemSync,
  getSentSync,
  getSettingsSync,
  getToSendSync,
  LATENCY,
  needsYouCountSync,
  reattachApproved,
  resendSent,
  restoreItem,
  restoreMany,
  sendItem,
  sentOpenCountSync,
  toggleProcedure,
  toSendCountSync,
  toSendTotalSync,
  touchOpen,
  usePreauthsVersion,
  waitForDoc,
  waitingDocCountSync,
  waitingDocTotalSync,
} from './store';
import { Header } from './components/Header';
import { ToSendList } from './components/ToSendList';
import { Detail } from './components/Detail';
import { SentDetail } from './components/SentDetail';
import { ApprovedTab, AutopilotSummary, SentTab } from './components/tabs';
import { ClosedView } from './components/ClosedView';
import { DontSendDialog } from './components/dialogs';
import { PMS } from '@/features/shared/pms';
import { isPracticeOwner } from '@/features/shared/permissions';

interface Toast {
  id: number;
  msg: string;
  undoLabel?: string;
  onUndo?: () => void;
}
type Dialog = { type: 'dontsend' } | null;

function useIsDesktop(): boolean {
  const [d, setD] = useState(() => (typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true));
  useEffect(() => {
    const m = window.matchMedia('(min-width: 1024px)');
    const h = () => { setD(m.matches); };
    m.addEventListener('change', h);
    return () => { m.removeEventListener('change', h); };
  }, []);
  return d;
}

export function PreAuths({ view: viewProp }: { view?: Tab }) {
  usePreauthsVersion();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  const settings = getSettingsSync();
  // Only owners and admins may turn a payer's procedure off from Don't send (§6).
  const isOwner = isPracticeOwner();
  const toSend = getToSendSync();
  const selected = id ? getItemSync(id) : null;
  // A /pre-auths/:id that isn't a To-send item may be a Sent item — open it read-only.
  const sentSelected = id && !selected ? getSentItemSync(id) : null;
  const view: Tab = id && selected ? 'to_send' : (sentSelected ? 'sent' : (viewProp ?? 'to_send'));
  // Closed is a history view that lives on the /pre-auths route behind ?tab=closed
  // (no new route). It only applies on the bare To-send tab, never on a detail.
  const isClosed = !id && viewProp === 'to_send' && searchParams.get('tab') === 'closed';

  const [toasts, setToasts] = useState<Toast[]>([]);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [sending, setSending] = useState(false);
  const [resending, setResending] = useState(false);
  const toastSeq = useRef(0);

  useEffect(() => {
    if (id) touchOpen(id);
  }, [id]);

  // open first to-send on a bare To-send tab (desktop, autopilot off)
  useEffect(() => {
    if (!isClosed && !id && isDesktop && viewProp === 'to_send' && !settings.autopilot && toSend.length > 0) {
      const first = toSend[0];
      if (first) void navigate(`/pre-auths/${first.id}`, { replace: true });
    }
  }, [isClosed, id, isDesktop, viewProp, settings.autopilot, toSend, navigate]);

  const addToast = (msg: string, undo?: { label: string; onUndo: () => void }) => {
    const tid = ++toastSeq.current;
    setToasts((t) => [...t, { id: tid, msg, ...(undo ? { undoLabel: undo.label, onUndo: undo.onUndo } : {}) }]);
    setTimeout(() => { setToasts((t) => t.filter((x) => x.id !== tid)); }, 8000);
  };

  const tabTo = (v: Tab | 'closed'): string =>
    v === 'sent' ? '/pre-auths/sent' : v === 'approved' ? '/pre-auths/approved' : v === 'closed' ? '/pre-auths?tab=closed' : '/pre-auths';
  const goTo = (itemId: string) => { void navigate(`/pre-auths/${itemId}`); };

  const nextToSend = (): ToSendItem | null => getToSendSync().find((t) => t.id !== id) ?? null;
  const afterLeave = () => {
    const n = nextToSend();
    if (n) goTo(n.id);
    else void navigate('/pre-auths');
  };

  // ---- send flows ----
  const doSend = (optionId?: string) => {
    if (!selected) return;
    const payer = selected.payer.short;
    const name = selected.firstName;
    const option = optionId ? selected.decision?.options.find((o) => o.id === optionId) : undefined;
    if (option?.effect === 'send_wait_doc') {
      // Nothing is sent: Paul stays in To send as "Waiting for the X-ray" until the X-ray arrives.
      waitForDoc(selected.id);
      addToast(`ELVA added a note to the Oct 27 appointment in ${PMS}. ${name}’s pre-auth is ready to send once the new X-ray is in.`);
      return;
    }
    setSending(true);
    setTimeout(() => {
      setSending(false);
      sendItem(selected.id, { sentence: `${payer} usually answers — ELVA will follow the answer.` });
      if (option?.effect === 'send_move_appt') {
        addToast(`Sent. Move ${name}’s appointment after Oct 27 in ${PMS}; ELVA re-checks the timing when it moves.`);
      } else {
        addToast(`Sent to ${payer}. ELVA will follow the answer.`);
      }
      afterLeave();
    }, LATENCY.send);
  };

  const doDontSend = (v: { stopPreparing: boolean }) => {
    if (!selected) return;
    const name = selected.patient;
    const removedExtra: string[] = [];
    dontSend(selected.id);
    if (v.stopPreparing) {
      const payer = settings.payers.find((p) => p.short === selected.payer.short);
      if (payer) {
        const ids = toggleProcedure(payer.id, selected.procedureGroupId, false);
        for (const rid of ids) if (rid !== selected.id) removedExtra.push(rid);
        addToast(`${payer.short} ${selected.procedureGroup}: off.`);
      }
    }
    setDialog(null);
    addToast(`Not sent. ${name}’s appointment is unchanged.`, {
      label: 'Undo',
      onUndo: () => {
        restoreItem(selected.id);
        if (removedExtra.length) restoreMany(removedExtra);
      },
    });
    afterLeave();
  };

  const header = (
    <Header
      view={view}
      closed={isClosed}
      settings={settings}
      toSendCount={toSendCountSync()}
      toSendTotal={toSendTotalSync()}
      needsCount={needsYouCountSync()}
      waitingCount={waitingDocCountSync()}
      waitingTotal={waitingDocTotalSync()}
      sentCount={sentOpenCountSync()}
      approvedCount={approvedCountSync()}
      tabTo={tabTo}
    />
  );

  // ---- Closed history (?tab=closed) ----
  if (isClosed) {
    return (
      <div className="pa pa-page">
        {header}
        <ClosedView />
        {renderToasts()}
      </div>
    );
  }

  // ---- Sent item: read-only detail with status history (Oct 17 fix) ----
  if (sentSelected) {
    const doResend = () => {
      setResending(true);
      setTimeout(() => {
        setResending(false);
        resendSent(sentSelected.id);
        addToast(`Resent to ${sentSelected.payerShort}. ELVA will follow the answer.`);
      }, LATENCY.send);
    };
    // A denial's "what now" is resolved inline on the page, never a modal
    // (Oct 18 fix: Jorge's missing-tooth denial).
    const doDecide = () => {
      decideSentItem(sentSelected.id);
      addToast(`${sentSelected.patient}’s pre-auth is closed.`);
      void navigate('/pre-auths/sent');
    };
    return (
      <div className="pa pa-page">
        {header}
        <div className="pa-body">
          <SentDetail
            key={sentSelected.id}
            item={sentSelected}
            resending={resending}
            onResend={doResend}
            onDecide={doDecide}
            onBack={() => { void navigate('/pre-auths/sent'); }}
          />
        </div>
        {renderToasts()}
      </div>
    );
  }

  // ---- Sent tab ----
  if (view === 'sent') {
    return (
      <div className="pa pa-page">
        {header}
        <SentTab
          rows={getSentSync()}
          onReview={(rid) => { void navigate(`/pre-auths/${rid}`); }}
          onOpen={(rid) => { void navigate(`/pre-auths/${rid}`); }}
        />
        {renderDialogs()}
        {renderToasts()}
      </div>
    );
  }
  // ---- Approved tab ----
  if (view === 'approved') {
    return (
      <div className="pa pa-page">
        {header}
        <ApprovedTab
          rows={getApprovedSync()}
          onReattach={(aid) => {
            // Mock rebook: ELVA attaches the kept approval to the new visit (D162).
            const rebookedDisplay = 'Jan 12';
            reattachApproved(aid, rebookedDisplay);
            addToast(`Attached to the ${rebookedDisplay} visit.`);
          }}
        />
        {renderToasts()}
      </div>
    );
  }

  // ---- To send: autopilot summary (board 6) ----
  if (settings.autopilot && !id) {
    if (toSend.length === 0) {
      return (
        <div className="pa pa-page">
          {header}
          <div className="pa-empty">
            <p className="pa-empty-title">Nothing needs you.</p>
            <p className="pa-meta">
              ELVA sent its pre-auths this week.{' '}
              <button type="button" className="pa-src" style={{ textDecoration: 'underline' }} onClick={() => { void navigate('/pre-auths/sent'); }}>See them in Sent</button>
            </p>
          </div>
          {renderToasts()}
        </div>
      );
    }
    return (
      <div className="pa pa-page">
        {header}
        <AutopilotSummary held={toSend} onReview={goTo} onSent={() => { void navigate('/pre-auths/sent'); }} />
        {renderToasts()}
      </div>
    );
  }

  // ---- To send: empty (autopilot off) ----
  if (toSend.length === 0 && !id) {
    return (
      <div className="pa pa-page">
        {header}
        <div className="pa-empty">
          <p className="pa-empty-title">Nothing to send.</p>
          <p className="pa-meta">ELVA checks each new appointment as it is booked.</p>
        </div>
        {renderToasts()}
      </div>
    );
  }

  const mobileClass = !isDesktop ? (id ? 'pa-two-detail' : 'pa-two-queue') : '';

  let detail: React.ReactNode = null;
  if (id) {
    if (!selected) {
      detail = <NotFoundInPage as="main" thing="pre-auth" id={id} listLabel="pre-auths" listTo="/pre-auths" />;
    } else {
      detail = (
        <Detail
          key={selected.id}
          item={selected}
          sending={sending}
          onSend={() => { doSend(); }}
          onChoose={(optionId) => { doSend(optionId); }}
          onDontSend={() => { setDialog({ type: 'dontsend' }); }}
          {...(!isDesktop ? { onBack: () => { void navigate('/pre-auths'); } } : {})}
        />
      );
    }
  }

  return (
    <div className={`pa pa-page ${mobileClass}`}>
      {header}
      <div className="pa-body">
        <ToSendList items={toSend} {...(id ? { selectedId: id } : {})} onSelect={goTo} />
        {detail}
      </div>
      {renderDialogs()}
      {renderToasts()}
    </div>
  );

  function renderDialogs() {
    if (!dialog || !selected) return null;
    return <DontSendDialog item={selected} isOwner={isOwner} onCancel={() => { setDialog(null); }} onConfirm={doDontSend} />;
  }

  function renderToasts() {
    if (toasts.length === 0) return null;
    return (
      <div className="pa-toasts">
        {toasts.map((t) => (
          <div key={t.id} className="pa-toast" role="status">
            <span>{t.msg}</span>
            {t.undoLabel ? (
              <button
                type="button"
                className="pa-toast-undo"
                onClick={() => {
                  t.onUndo?.();
                  setToasts((list) => list.filter((x) => x.id !== t.id));
                }}
              >
                {t.undoLabel}
              </button>
            ) : null}
          </div>
        ))}
      </div>
    );
  }
}
